import {
  createGuid,
  PolygonHierarchy,
  Color,
  Cartographic,
  Cartesian2,
  Cartesian3,
  DistanceDisplayCondition,
  sampleTerrainMostDetailed,
  BoundingSphere,
  PolygonPipeline,
  VerticalOrigin,
  //CesiumTerrainProvider,
  //TerrainProvider,
  GeometryInstance,
  Primitive,
  MaterialAppearance,
  Material,
  PolygonGeometry,
  Math as CesiumMath,
  //CzmlDataSource,
  //ClassificationType
} from "@cesium/engine";

import VolumePlanHeightType from "../const/VolumePlanHeightType";
import VolumeClassificationType from "../const/VolumeClassificationType";
import DitapViewer from "../basic/DitapViewer";
import Utils from "../basic/Utils";
import ConstantUrl from "../icon/ConstantUrl";
import DitapLoading from "../popup/DitapLoading";

const DEFAULT_OPTION = {
  planHeight: VolumePlanHeightType.AVERAGE,
  cellSize: 1,
  showCell: false,
  fillingVolumeColor: Color.fromBytes(85, 25, 220, 35),
  cuttingVolumeColor: Color.fromBytes(220, 25, 105, 35),
  planHeightVolumeColor: Color.GRAY.withAlpha(0.3),
  callback: () => {},
  classification: VolumeClassificationType.TERRAIN,
  loading: true,
};

// const VOLUME_OPTIONS = {
//   POSITIONS: [],
//   CELL_SIZE: 1,
//   PLAN_HEIGHT: PLAN_HEIGHT_TYPE.AVERAGE,
//   CALLBACK: undefined
// }

/**
 * 체적량 계산 및 영역 표시 클래스. </br>
 * Volumetric Calculation and Region Display Class. </br>
 * </br>
 * ※ 주의사항(Note). </br>
 * 측정 범위가 cellSize^2 * 10000을 초과할 경우 기능 중단 가능성이 있음. </br>
 * Please be aware that if the measurement range exceeds cellSize^2 * 10000, there is a possibility of functionality interruption.
 *
 * @alias MeasurementVolume
 * @constructor
 *
 * @param {object} options 각각 다음과 같은 속성을 가진 객체. </br> Object with the following properties:
 * @param {DitapViewer} options.viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
 * @param {Color} [options.entityColor = Color.fromBytes(229, 30, 110, 255)] 엔티티 색상.<br> Color of the entity.
 * @param {Cartesian3[]} [options.positions=[]] 측정 영역 좌표. </br> Positions
 * @param {VolumePlanHeightType | number} [options.planHeight=VolumePlanHeightType.AVERAGE] 계획고 타입 [VolumePlanHeightType.MIN, VolumePlanHeightType.MAX, VolumePlanHeightType.AVERAGE] 또는 높이 값 (미터). </br> Plan height type [VolumePlanHeightType.MIN, VolumePlanHeightType.MAX, VolumePlanHeightType.AVERAGE] or plan height value (meters).
 * @param {number} [options.cellSize=1] 셀 사이즈 (미터).  </br> CellSize (meters).
 * @param {boolean} [options.showCell=false] 셀 가시화. </br> Visualize the cell
 * @param {Color} [options.fillingVolumeColor=Color.fromBytes(85, 25, 220, 30)] 성토 셀 색상. </br> FillVolume cell color
 * @param {Color} [options.cuttingVolumeColor=Color.fromBytes(220, 25, 105, 30)] 절토 셀 색상. </br> CuttingVolume cell color
 * @param {VolumeClassificationType} [options.classification=VolumeClassificationType.TERRAIN] 높이 추출 유형 [VolumeClassificationType.TERRIN, VolumeClassificationType.BOTH]. </br> Height value extraction type [VolumeClassificationType.TERRIN, VolumeClassificationType.BOTH].
 * @param {boolean} [options.loading=true] 계산 중 로딩창. </br> Loading page during calculation
 * @param {Function} [options.callback=undefined] 계산 종료 후 콜백 함수. </br> Callback function after volume measurement
 *
 * @example
 * const getUrlFromBase64 = (base64Data) => {
 *   const b64Data = base64Data;
 *   const byteCharacters = window.atob(b64Data);
 *   const byteNumbers = new Array(byteCharacters.length);
 *   for (let i = 0; i < byteCharacters.length; i++) {
 *     byteNumbers[i] = byteCharacters.charCodeAt(i);
 *   }
 *   const byteArray = new Uint8Array(byteNumbers);
 *   const blob = new Blob([byteArray], { type: "image/png" });
 *
 *   return URL.createObjectURL(blob);
 * };
 *
 * const viewer = new Ditap.DitapViewer("ditapContainer", {
 *   baseLayerPicker: true,
 *   // 지형(터레인 지정)
 *   terrainProviderViewModels: [
 *     new Ditap.ProviderViewModel({
 *       name: "Ditap Korea Terrain",
 *       iconUrl: getUrlFromBase64(
 *         Ditap.ConstantUrl.DITAP_TERRAIN_IMG_URL
 *       ),
 *       tooltip: "WGS84 standard ellipsoid, also known as EPSG:4326",
 *       category: "Ditap",
 *       creationFunction: function () {
 *         return Ditap.NationwideTerrainProvider.create();
 *       },
 *     }),
 *   ],
 * });
 *
 * const destination = Ditap.Cartesian3.fromDegrees(
 *   126.978275264,
 *   37.566642192,
 *   400
 * );
 *
 * // 카메라 이동
 * viewer.scene.camera.flyTo({
 *   destination: destination,
 * });
 *
 * //1. 지정된 영역 생성
 * const positions = [
 *   new Ditap.Cartesian3(-3044739.446567739, 4043823.403143691,3867463.012077135),
 *   new Ditap.Cartesian3(-3044747.714411474, 4043814.3380325786,3867466.015981707),
 *   new Ditap.Cartesian3(-3044752.780860346, 4043817.3342751344,3867458.802892699),
 * ];
 * const options = {
 *   viewer: viewer,
 *   positions: positions,                                     // 영역 좌표
 *   // entityColor: Color.fromBytes(229, 30, 110, 255)         // 엔티티 색상
 *   // planHeight: Ditap.VolumePlanHeightType.AVERAGE,        // 계획고 타입
 *   // cellSize: 1,                                           // 분석 해상도
 *   // showCell: false,                                       // 셀 가시화 여부
 *   // fillingVolumeColor: Color.fromBytes(85, 25, 220, 35),     // 성토 셀 색상
 *   // cuttingVolumeColor: Color.fromBytes(220, 25, 105, 35), // 절토 셀 색상
 *   // planHeightVolumeColor: Color.GRAY.withAlpha(0.3),      // 0 셀 컬러
 *   // callback: () => {},                                    // 콜백 함수
 *   // classification: VolumeClassificationType.TERRAIN,      // 분석 타입
 *   // loading: true,                                         // 로딩 창 여부
 * };
 * const measurementVolume = new Ditap.MeasurementVolume(viewer, options);
 *
 * //2. 체적 재측정
 * // measurementValume.calculateVolume(options);
 *
 * //3. 마우스로 영역 지정 후 측정
 * const measurementVolume = new Ditap.MeasurementVolume({viewer: viewer});
 * const interactionVolume = new Ditap.InteractionVolume(viewer);
 * interactionVolume.measurementVolume = measurementVolume;
 * interactionVolume.on();
 *
 */
class MeasurementVolume {
  constructor(options) {
    this._fillingVolume = undefined; // 성토량
    this._cuttingVolume = undefined; // 절토량
    this._totalVolume = undefined; // 성토량 + 절토량
    this._pointEntities = [];
    this._positions = [];
    this._polylineEntity = undefined;
    this._cellPrimitive = [];
    this._polygonPrimitive = undefined;
    this._labelEntity = undefined;
    this._id = `MeasurementVolume${createGuid()}`;
    this._resizeObserver = undefined;
    this._windowResizeEvent = undefined;
    this._centerPosition = undefined;
    this._clientHeight = undefined;
    this._entityNum = 0;
    this._cameraChangedEvent = undefined;
    this._isReady = false;
    this._displayCondition = 2000;
    this._ditapLoading = new DitapLoading();
    this.waitIconUrl = Utils.getUrlFromBase64(ConstantUrl.VOLUME_ICON_URL);

    this._viewer = undefined;
    if (options.viewer instanceof DitapViewer) {
      this._viewer = options.viewer;
      this._viewer.ditapStorage.measurementVolume[this._id] = this;
    } else {
      throw Error("MeasurementVolume viewer type error");
    }

    this._entityColor = undefined;
    if (options.entityColor instanceof Color) {
      this._entityColor = options.entityColor;
    } else {
      this._entityColor = Color.fromBytes(229, 30, 110, 255);
    }

    if (!options) {
      options = {};
    }

    this._planHeight = DEFAULT_OPTION.planHeight;
    if (Object.values(VolumePlanHeightType).includes(options.planHeight)) {
      this._planHeight = options.planHeight;
    } else if (!isNaN(options.planHeight)) {
      this._planHeight = Number(options.planHeight);
    }

    this._cellSize = DEFAULT_OPTION.cellSize;
    if (!isNaN(options.cellSize)) {
      this._cellSize = Number(options.cellSize);
    }

    this._showCell = DEFAULT_OPTION.showCell;
    if (typeof options.showCell === "boolean") {
      this._showCell = options.showCell;
    }

    this._fillingVolumeColor = DEFAULT_OPTION.fillingVolumeColor;
    if (options.fillingVolumeColor instanceof Color) {
      this._fillingVolumeColor = options.fillingVolumeColor;
    }

    this._cuttingVolumeColor = DEFAULT_OPTION.cuttingVolumeColor;
    if (options.cuttingVolumeColor instanceof Color) {
      this._cuttingVolumeColor = options.cuttingVolumeColor;
    }

    this._callback = DEFAULT_OPTION.callback;
    if (options.callback instanceof Function) {
      this._callback = options.callback;
    }

    this._classification = DEFAULT_OPTION.classification;
    if (
      Object.values(VolumeClassificationType).includes(options.classification)
    ) {
      this._classification = options.classification;
    }

    this._loading = DEFAULT_OPTION.loading;
    if (typeof options.loading === "boolean") {
      this._loading = options.loading;
    }

    // positions 값이 있을 경우
    if (options.positions) {
      this.calculateVolume(options);
    }
  }

  /**
   * 사용할 Ditap Viewer 인스턴스.</br>
   * The DitapViewer instance to use.
   * @type {DitapViewer}
   */
  get viewer() {
    return this._viewer;
  }
  set viewer(viewer) {
    if (viewer instanceof DitapViewer) {
      this._viewer = viewer;
    }
  }

  /**
   * 측정 객체 ID. </br>
   * ID to assign to the measurement object.
   * @readonly
   * @type {string}
   */
  get id() {
    return this._id;
  }

  /**
   * 측정 영역 좌표. </br>
   * Measurement area coordinates
   * @readonly
   * @type {Cartesian3[]}
   */
  get positions() {
    return this._positions;
  }

  /**
   * 계획고. </br>
   * Volume plan height.
   * @readonly
   * @type {string}
   */
  get planHeight() {
    return this._planHeight;
  }

  /**
   * 성토량. </br>
   * Fill volume.
   * @readonly
   * @type {string}
   */
  get fillingVolume() {
    return this._fillingVolume;
  }

  /**
   * 절토량. </br>
   * Cutting volume.
   * @readonly
   * @type {string}
   */
  get cuttingVolume() {
    return this._cuttingVolume;
  }

  /**
   * 성토량과 절토량의 합. </br>
   * Sum of cut and fill volume.
   * @readonly
   * @type {string}
   */
  get totalVolume() {
    return this._totalVolume;
  }

  /**
   * 측정 영역 포인트 엔티티. </br>
   * Volume area point entity.
   * @type {Entity[]}
   */
  get pointEntities() {
    return this._pointEntities;
  }
  set pointEntities(pointEntities) {
    this._pointEntities = pointEntities;
  }

  /**
   * 측정 영역 폴리라인 엔티티. </br>
   * Volume area polyline entity.
   * @type {Entity}
   */
  get polylineEntity() {
    return this._polylineEntity;
  }
  set polylineEntity(polylineEntity) {
    this._polylineEntity = polylineEntity;
  }

  /**
   * 측정 영역 polygon primitive. </br>
   * Volume area polygon primitive.
   * @type {Primitive}
   */
  get polygonPrimitive() {
    return this._polygonPrimitive;
  }
  set polygonPrimitive(polygonPrimitive) {
    this._polygonPrimitive = polygonPrimitive;
  }

  /**
   * 측정 영역 셀 Primitive. </br>
   * Volume area cell primitive.
   * @type {Primitive[]}
   */
  get cellPrimitive() {
    return this._cellPrimitive;
  }
  set cellPrimitive(cellPrimitive) {
    this._cellPrimitive = cellPrimitive;
  }

  /*
   * 측정 영역 라벨 엔티티. </br>
   * Volume area label entity.
   * @type {Entity}
   */
  get labelEntity() {
    return this._labelEntity;
  }
  set labelEntity(labelEntity) {
    this._labelEntity = labelEntity;
  }

  /**
   * 계획고 타입. </br>
   * Plane hegith type.
   * @readonly
   * @type {string}
   */
  get planHeightType() {
    return this._planHeightType;
  }

  /*
   * 윈도우 크기 조절 시 실행 이벤트.</br>
   * Events to run when window resizing.
   * @type {Function}
   */
  get windowResizeEvent() {
    return this._windowResizeEvent;
  }
  set windowResizeEvent(windowResizeEvent) {
    this._windowResizeEvent = windowResizeEvent;
  }

  /**
   * 측정 영역 중심점. </br>
   * Volume area center position.
   * @readonly
   * @type {Cartesian3}
   */
  get centerPosition() {
    return this._centerPosition;
  }

  /*
   * 사용자 윈도우 높이. </br>
   * Client window height.
   * @type {number}
   */
  get clientHeight() {
    return this._clientHeight;
  }
  set clientHeight(clientHeight) {
    this._clientHeight = clientHeight;
  }

  /*
   * 현재 객체에 속한 엔티티 수. </br>
   * Volume entity count.
   * @type {number}
   * @private
   */
  get entityNum() {
    return this._entityNum;
  }
  set entityNum(entityNum) {
    this._entityNum = entityNum;
  }

  /*
   * 카메라 변경 이벤트 제거.</br>
   * Camera change event remover.
   * @type {Function}
   */
  get cameraChangedEvent() {
    return this._cameraChangedEvent;
  }
  set cameraChangedEvent(cameraChangedEvent) {
    this._cameraChangedEvent = cameraChangedEvent;
  }

  /*
   * 엔티티의 DistanceDisplayCondition 값. </br>
   * DistanceDisplayCondition value of entity.
   * @type {number}
   */
  set displayCondition(displayCondition) {
    this._displayCondition = displayCondition;
  }
  get displayCondition() {
    return this._displayCondition;
  }

  /**
   * 계산 완료 여부. </br>
   * Check that the volume calculation is complete.
   * @readonly
   * @type {boolean}
   */
  get isReady() {
    return this._isReady;
  }

  /**
   * 셀 사이즈. </br>
   * Volume cell size.
   * @readonly
   * @type {number}
   */
  get cellSize() {
    return this._cellSize;
  }

  /**
   * 계산 종료 후 콜백 함수. </br>
   * Callback function after volume measurement
   * @readonly
   * @type {Function | undefined}
   */
  get callback() {
    return this._callback;
  }

  /**
   * 셀 가시화. </br>
   * Visualize volume cell
   * @readonly
   * @type {boolean}
   */
  get showCell() {
    return this._showCell;
  }

  /**
   * 성토량 셀 색상.<br>
   * Fill volume cell color.
   * @readonly
   * @type {Color}
   */
  get fillingVolumeColor() {
    return this._fillingVolumeColor;
  }

  /**
   * 절토량 셀 색상.<br>
   * Cutting volume cell color.
   * @readonly
   * @type {Color}
   */
  get cuttingVolumeColor() {
    return this._cuttingVolumeColor;
  }

  /**
   * 높이 추출 유형. </br>
   * Height value extraction type.
   * @readonly
   * @type {number}
   */
  get classification() {
    return this._classification;
  }

  /**
   * 계산 중 로딩창. </br>
   * Loading page during calculation.
   * @readonly
   * @type {boolean}
   */
  get loading() {
    return this._loading;
  }

  /**
   * 엔티티 색상.<br>
   * Color of the entity.
   */
  set entityColor(entityColor) {
    if (entityColor instanceof Color) {
      this._entityColor = entityColor;
      Utils.changePointColor(this.pointEntities, entityColor);
      Utils.changePolylineColor([this.polylineEntity], entityColor);
      Utils.changePolygonPrimitiveColor([this.polygonPrimitive], entityColor);
    }
  }
  get entityColor() {
    return this._entityColor;
  }

  /**
   * 측정 영역으로 카메라 이동. </br>
   * Move camera to volume area.
   */
  zoomToVolume() {
    const viewer = this.viewer;
    // const entity = this.polygonEntity;
    // if (entity instanceof Entity) {
    //   viewer.zoomTo(entity);
    // }
    const position = this._centerPosition;
    if (position instanceof Cartesian3) {
      const ctg = Cartographic.fromCartesian(this._centerPosition);
      ctg.height += 100;
      viewer.camera.flyTo({
        destination: Cartographic.toCartesian(ctg),
        duration: 2,
        orientation: {
          heading: CesiumMath.toRadians(0),
          pitch: CesiumMath.toRadians(-90),
          roll: 0,
        },
      });
    }
  }

  // /*
  //  * 폴리곤 엔티티 생성. </br>
  //  * Create a PolygonEntity in the DitapViewer.
  //  * @param {Cartesian3[]} positions 폴리곤 좌표. </br> Cartesians of polygonEntity.
  //  * @param {MaterialProperty | Color} material 폴리곤 재질. </br> A Property specifying the material used to draw the polygon.
  //  * @returns {Entity} 생성된 폴리곤 엔티티. </br> Created polygon entity.
  //  */
  // createPolygon(positions, material = Color.WHITE.withAlpha(0.3)) {
  //   const id = this._id + this.entityNum;

  //   const polyGeometry = new PolygonGeometry({
  //     polygonHierarchy: positions,
  //     perPositionHeight: true,
  //   });

  //   const polyInstance = new GeometryInstance({
  //     geometry: polyGeometry,
  //     id: id,
  //     attributes: {
  //       // color: ColorGeometryInstanceAttribute.fromColor(material),
  //       // depthFailColor: ColorGeometryInstanceAttribute.fromColor(Color.GRAY.withAlpha(0.3)),
  //       distanceDisplayCondition: new DistanceDisplayConditionGeometryInstanceAttribute(
  //         0,
  //         this.displayCondition
  //       ),
  //     },
  //   });

  //   const primitive = new Primitive({
  //     geometryInstances: polyInstance,
  //     appearance: new MaterialAppearance({
  //       material: new Material({
  //         fabric: {
  //           type: "Color",
  //           uniforms: {
  //             color: material,
  //           },
  //         },
  //       }),
  //     }),
  //     depthFailAppearance: new MaterialAppearance({
  //       material: new Material({
  //         fabric: {
  //           type: "Color",
  //           uniforms: {
  //             color: Color.GRAY.withAlpha(0.3),
  //           },
  //         },
  //       }),
  //     }),
  //     releaseGeometryInstances: false,
  //   });

  //   this.viewer.scene.primitives.add(primitive);

  //   // primitive.update();

  //   // this.viewer.scene.groundPrimitives.add(primitive);

  //   // this.viewer.scene.primitives.raiseToTop(primitive);
  //   // this.viewer.scene.primitives.lowerToBottom(primitive);

  //   return primitive;
  //   // }
  // }

  /*
   * 셀 엔티티 생성. </br>
   * Create cell Entity
   * @private
   * @param {Cartesian3[]} positions 셀 좌표. </br> Cell area positions
   * @param {number} groundHeight 생성 높이. </br> Ground level at which cells are created
   * @param {number} cellHeight 셀 높이. </br> Cell height
   * @returns {Primitive} 생성된 셀 엔티티. </br> Created cell entity
   */
  createCell(positions, groundHeight, cellHeight, color) {
    const viewer = this.viewer;
    const depthColor = new Color(
      color.red,
      color.green,
      color.blue,
      color.alpha
    ).withAlpha(0.05);
    const id = this._id + this.entityNum;
    this.entityNum++;

    const polygon = new GeometryInstance({
      id: id,
      geometry: new PolygonGeometry({
        polygonHierarchy: new PolygonHierarchy(positions),
        //perPositionHeight: true,
        extrudedHeight: groundHeight + cellHeight,
        height: groundHeight,
      }),
    });

    const primitive = new Primitive({
      geometryInstances: polygon,
      appearance: new MaterialAppearance({
        material: new Material({
          fabric: {
            type: "Color",
            uniforms: {
              color: color,
            },
          },
        }),
      }),
      depthFailAppearance: new MaterialAppearance({
        material: new Material({
          fabric: {
            type: "Color",
            uniforms: {
              color: depthColor,
            },
          },
        }),
      }),
      releaseGeometryInstances: false,
    });

    const result = viewer.scene.primitives.add(primitive);

    return result;
  }

  // /**
  //  * Create cell entity
  //  * @param {Cartesian3[]} positions 좌표
  //  * @param {number} height Cell 높이
  //  * @param {Color} color Cell 생상
  //  * @returns {Entity} Cell Entity
  //  */
  // createCellEntity(positions, height, color) {
  //   const viewer = this.viewer;
  //   const id = this._id + this.entityNum;
  //   //const id = `${this._id}_${this.entityNum}`;
  //   this.entityNum++;
  //   const cell = viewer.entities.add({
  //     id: id || undefined,
  //     polygon: {
  //       hierarchy: positions,
  //       material: color,
  //       perPositionHeight: true,
  //       height: 0,
  //       extrudedHeight: height,
  //     },
  //   });

  //   return cell;
  // }

  /*
   * 대기 라벨 생성. </br>
   * Create label, when waiting to calculate.
   * @private
   * @param {Cartesian3} position 대기 라벨 좌표. </br> Label position.
   * @returns {Entity} 대기 라벨 엔티티. </br> Label entity.
   */
  createWaitLabel(position) {
    const viewer = this.viewer;
    const id = this._id + this.entityNum;
    this.entityNum++;
    const entity = viewer.entities.add({
      position: position,
      id: id,
      billboard: {
        image: this.waitIconUrl,
        verticalOrigin: VerticalOrigin.BOTTOM,
        distanceDisplayCondition: new DistanceDisplayCondition(
          0,
          this.displayCondition
        ),
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      },
    });

    return entity;
  }

  /*
   * 엔티티 생성. </br>
   * Create volume area point, polyline, polygon, label.
   * @private
   * @param {Cartesian3[]} positions 좌표. </br> Volume position.
   */
  createEntity(positions) {
    // 포인트 생성
    for (let i = 0; i < positions.length; i++) {
      const pointOPtion = {
        viewer: this.viewer,
        id: this._id + this.entityNum,
        position: positions[i],
        outlineColor: this.entityColor,
        displayCondition: this.displayCondition,
      };
      this.entityNum++;
      const point = Utils.createPoint(pointOPtion);
      this.pointEntities.push(point);
    }

    // 폴리라인 생성
    const polylineOption = {
      viewer: this.viewer,
      id: this._id + this.entityNum,
      positions: [...positions, positions[0]],
      material: this.entityColor,
      displayCondition: this.displayCondition,
    };
    this.entityNum++;
    const polyline = Utils.createPolyline(polylineOption);
    this.polylineEntity = polyline;

    // 폴리곤 생성
    const polygon = Utils.createPolygonPrimitive({
      viewer: this.viewer,
      id: this._id + this.entityNum,
      positions: positions,
      material: this.entityColor.withAlpha(0.3),
      displayCondition: this.displayCondition,
    });
    this.polygonPrimitive = polygon;
  }

  /**
   * 초기화. </br>
   * Initializing volume values and entities.
   */
  clear() {
    const viewer = this.viewer;

    this._positions = [];
    this._planHeight = undefined;
    this._fillingVolume = undefined;
    this._cuttingVolume = undefined;
    this._totalVolume = undefined;
    for (const entity of this.pointEntities) {
      viewer.entities.remove(entity);
    }
    this.pointEntities = [];

    viewer.entities.remove(this.polylineEntity);
    this.polylineEntity = undefined;

    // viewer.entities.remove(this.polygonEntity);
    // this.polygonEntity = undefined;

    if (this.polygonPrimitive) {
      this.viewer.scene.primitives.remove(this.polygonPrimitive);
    }
    this.polygonPrimitive = undefined;

    if (this.cellPrimitive) {
      for (const primitive of this.cellPrimitive) {
        this.viewer.scene.primitives.remove(primitive);
      }
    }
    this.cellPrimitive = [];

    //delete this.viewer.ditapStorage.measurementVolume[this._id];

    if (this.labelEntity instanceof Element) {
      this.labelEntity.remove();
    }
    this.labelEntity = undefined;

    this.viewer = undefined;
    this._centerPosition = undefined;
    this._isReady = false;
    this._planHeightType = "average";
  }

  /**
   * 인스턴스 제거. </br>
   * Destroys the instance.
   */
  destroy() {
    if (this.labelEntity instanceof Element) {
      this.labelEntity?.remove();
    }

    if (this.label instanceof Element) {
      this.label.removeEventListener("mouseover", Utils.hideLabelElement);
      this.label.removeEventListener("mouseover", Utils.showLabelElement);
    }

    this.labelEntity = undefined;
    this.windowResizeEvent = undefined;
    //this.popupMeasurementVolume.destroy();
    // viewerStorage에서 삭제
    this.viewer.ditapStorage.removeInstanceById(this._id);
    this.removeChangeEvent();
    this.clear();
  }

  /*
   * 폴리라인1(p1,p2)~폴리라인2(p3,p4) 교차 여부. </br>
   * Polyline1(p1,p2)~Polyline2(p3,p4) Intersection.
   * @param {Cartesian2} p1 폴리라인1 시작점. </br> Polyline1 start position.
   * @param {Cartesian2} p2 폴리라인1 종료점. </br> Polyline1 end position.
   * @param {Cartesian2} p3 폴리라인2 시작점. </br> Polyline2 start position.
   * @param {Cartesian2} p4 폴리라인2 종료점. </br> Polyline2 end position.
   * @returns {Boolean} 교차 여부. </br> Interaction state
   */
  lineIntersection(p1, p2, p3, p4) {
    // 기울기가 같은 일직선일 떄 [p1, p2], [p3, p4]의 범위가 중복될 경우 교차함
    if (Math.max(p1.x, p2.x) <= Math.min(p3.x, p4.x)) {
      return false;
    }
    if (Math.min(p1.x, p2.x) >= Math.max(p3.x, p4.x)) {
      return false;
    }
    if (Math.max(p1.y, p2.y) <= Math.min(p3.y, p4.y)) {
      return false;
    }
    if (Math.min(p1.y, p2.y) >= Math.max(p3.y, p4.y)) {
      return false;
    }

    const sign1 = (p2.x - p1.x) * (p3.y - p1.y) - (p3.x - p1.x) * (p2.y - p1.y);
    const sign2 = (p2.x - p1.x) * (p4.y - p1.y) - (p4.x - p1.x) * (p2.y - p1.y);

    const sign3 = (p4.x - p3.x) * (p1.y - p3.y) - (p1.x - p3.x) * (p4.y - p3.y);
    const sign4 = (p4.x - p3.x) * (p2.y - p3.y) - (p2.x - p3.x) * (p4.y - p3.y);

    if (sign1 === 0 && sign2 === 0 && sign3 === 0 && sign4 === 0) {
      return true;
    }

    return sign1 * sign2 < 0 && sign3 * sign4 < 0;
  }

  /*
   * 폴리곤1~폴리곤2 교차 여부. </br>
   * Polygon1~Polygon2 Intersection.
   * @private
   * @param {Cartesian2[]} polygon1 폴리곤1 좌표. </br> Polygon1 position.
   * @param {Cartesian2[]} polygon2 폴리곤2 좌표. </br> Polygon2 position.
   * @returns {Boolean} 교차 여부. </br> Interaction state
   */
  intersection(polygon1, polygon2) {
    for (let i = 0; i < polygon1.length; i++) {
      for (let j = 0; j < polygon2.length; j++) {
        const point1 = polygon1[i];
        const point2 =
          i === polygon1.length - 1 ? polygon1[0] : polygon1[i + 1];
        const point3 = polygon2[j];
        const point4 =
          j === polygon2.length - 1 ? polygon2[0] : polygon2[j + 1];

        // true인 경우 교차하는 선분이 있기에 return true;
        if (this.lineIntersection(point1, point2, point3, point4)) {
          return true;
        }
      }
    }

    return false;
  }

  /*
   * Polygon의 Point 포함 여부. </br>
   * Point in Polygon.
   * @private
   * @param {Cartesian2[]} polygon 폴리곤 좌표. </br> Polygon position.
   * @param {Cartesian2} point 포인트 좌표. </br> Point position.
   * @returns {Boolean} 포함 여부. </br> Contain state
   */
  contain(polygon, point) {
    let count = 0; // point와 polygon X선분 사이의 교점 수
    for (let i = 0; i < polygon.length; i++) {
      const j = (i + 1) % polygon.length;
      //point가 선분 (polygon[i], polygon[j])의 y좌표 사이에 있음
      if (polygon[i].y > point.y !== polygon[j].y > point.y) {
        //point <-> [ polygon[i], polygon[j] ] 교점
        const intersectionPoint =
          ((polygon[j].x - polygon[i].x) * (point.y - polygon[i].y)) /
            (polygon[j].y - polygon[i].y) +
          polygon[i].x;

        //교점이 point보다 크면 오른쪽에 있는 교점임
        if (point.x < intersectionPoint) {
          count++;
        }
      }
    }

    return count % 2 > 0;
  }

  /*
   * 가로 X 세로 영역을 포함한 모든 셀의 중심점 추출. </br>
   * Extraction of the center points of all cells including the width X height area.
   * @private
   * @param {Cartesian3[]} positions 영역 좌표. </br> Area position.
   * @param {number} cellSize 셀 사이즈. </br> Cell size.
   * @param {number} classification 분석 타입. </br> Classification type
   * @returns {Object[]} 셀 정보. </br> Cell info
   */
  async generateCellInfo(positions, cellSize, classification) {
    // 테스트 코드
    // positions = [
    //   new Cartesian3(-3051173.320720258, 4049930.868722122, 3856079.0283566318),
    //   new Cartesian3(-3051285.4997382862, 4049904.088390052,3856013.9330656594),
    //   new Cartesian3(-3051262.48137076, 4049994.895652945, 3855939.743549114),
    //   new Cartesian3(-3051176.2362817763, 4050026.372925856, 3855977.6501829023)
    // ];
    // 1. MBR 추출
    const mbr = this.getMBR(positions);
    if (!mbr) {
      throw Error("MeasurementVolume getMBR undefined");
    }

    // 2. row, col vector 계산
    const rowVector = Cartesian3.subtract(mbr[1], mbr[0], new Cartesian3());
    const rowVector_normal = Cartesian3.normalize(rowVector, new Cartesian3());
    const colVector = Cartesian3.subtract(mbr[2], mbr[0], new Cartesian3());
    const colVector_normal = Cartesian3.normalize(colVector, new Cartesian3());

    // 3. row, col 길이 계산
    const rowDistance = Cartesian3.distance(mbr[0], mbr[1]);
    const colDistance = Cartesian3.distance(mbr[0], mbr[2]);

    // 4. row, col Index 계산
    let rowIndex = Math.ceil(colDistance / cellSize); // row 줄 수
    let colIndex = Math.ceil(rowDistance / cellSize); // col 줄 수
    const rowRemain = rowDistance % cellSize;
    const colRemain = colDistance % cellSize;

    // 나머지값이 있으면 index +1
    rowIndex = rowRemain === 0 ? rowIndex : rowIndex + 1;
    colIndex = colIndex === 0 ? colIndex : colIndex + 1;

    const rowShift = rowRemain === 0 ? cellSize / 2 : rowRemain / 2;
    const colShift = colRemain === 0 ? cellSize / 2 : colRemain / 2;

    // 5. 시작점 계산
    const rowVal = Cartesian3.multiplyByScalar(
      rowVector_normal,
      rowShift,
      new Cartesian3()
    );
    const colVal = Cartesian3.multiplyByScalar(
      colVector_normal,
      colShift,
      new Cartesian3()
    );
    const first = Cartesian3.add(mbr[0], rowVal, new Cartesian3());
    Cartesian3.add(first, colVal, first);

    // 6. 셀 정보 생성
    const rowArr = []; // [col_arr1, col_arr2, col_arr3...]
    let totalHeight = 0;
    let minHeight;
    let maxHeight;
    for (let row = 0; row < rowIndex; row++) {
      // 각 행의 시작점
      const firstValue = Cartesian3.multiplyByScalar(
        colVector_normal,
        row * cellSize,
        new Cartesian3()
      );
      const rowFirst = Cartesian3.add(first, firstValue, new Cartesian3());

      for (let col = 0; col < colIndex; col++) {
        // 가로 방향 벡터 * count
        const value = Cartesian3.multiplyByScalar(
          rowVector_normal,
          col * cellSize,
          new Cartesian3()
        );
        const center = Cartesian3.add(rowFirst, value, new Cartesian3()); // 셀 중심 좌표
        const edge = this.getCellEdge(
          center,
          rowVector_normal,
          colVector_normal,
          cellSize / 2,
          cellSize / 2
        ); // 셀 좌상단, 우하단 좌표

        // Cell Contain && Intersection Check
        let isAvailable = this.contain(
          positions.map((pos) => this.cartographicToCartesian(pos)),
          this.cartographicToCartesian(center)
        );
        if (!isAvailable) {
          isAvailable = this.intersection(
            positions.map((pos) => this.cartographicToCartesian(pos)),
            edge.map((pos) => this.cartographicToCartesian(pos))
          );
        }

        // contain && intersection
        if (isAvailable) {
          rowArr.push({
            center: center,
            edge: edge,
          });
        }
      }
    }

    // 7. 각 셀들의 높이 추출 => 최소, 최대, 평균 높이 계산
    // 단일처리
    // for await (const cell of rowArr) {
    //   const cellHeight = await this.calcCellAvgHeight(
    //     [...cell.edge],
    //     classification
    //   );

    //   if (maxHeight === undefined) {
    //     maxHeight = cellHeight;
    //   }

    //   if (minHeight === undefined) {
    //     minHeight = cellHeight;
    //   }

    //   totalHeight += cellHeight; // 전체 높이 합
    //   maxHeight = maxHeight < cellHeight ? cellHeight : maxHeight; // 최대 높이
    //   minHeight = cellHeight < minHeight ? cellHeight : minHeight; // 최소 높이

    //   cell["height"] = cellHeight;
    // }
    // 병렬처리
    try {
      const promises = rowArr.map(async (cell) => {
        const cellHeight = await this.calcCellAvgHeight(
          [...cell.edge],
          classification
        );

        if (maxHeight === undefined) {
          maxHeight = cellHeight;
        }

        if (minHeight === undefined) {
          minHeight = cellHeight;
        }

        totalHeight += cellHeight; // 전체 높이 합
        maxHeight = maxHeight < cellHeight ? cellHeight : maxHeight; // 최대 높이
        minHeight = cellHeight < minHeight ? cellHeight : minHeight; // 최소 높이

        // 현재 셀 높이 설정
        cell["height"] = cellHeight;
      });

      // 전체 작업 완료 대기
      await Promise.all(promises);
    } catch (error) {
      throw Error(`MeasurementVolume generateCellInfo type error : ${error}`);
    }

    // 8. 평균높이 계산
    let average = 0;
    const averageValue = totalHeight / rowArr.length;
    if (averageValue !== Infinity && !isNaN(averageValue)) {
      average = averageValue;
    }

    return {
      min: minHeight,
      max: maxHeight,
      average: average,
      cellInfos: rowArr,
    };
  }

  /*
   * 입력 셀 중심점을 통해 꼭짓점 좌표 추출. </br>
   * Extract corner positions through the input cell center point.
   * @private
   * @param {Cartesian3} center 중심좌표. </br> Cell center position.
   * @param {Cartesian3} rowVector 수평 벡터. </br> Horizontal vector.
   * @param {Cartesian3} colVector 수직 벡터. </br> Vertical vector.
   * @param {number} cellRowDistance 수평 길이 / 2. </br> Horizontal length / 2.
   * @param {number} cellColDistance 수직 길이 / 2. </br> Vertical vector / 2.
   * @returns {Cartesian3[]} 꼭짓점 좌표. </br> Cell corner positions
   */
  getCellEdge(center, rowVector, colVector, cellRowDistance, cellColDistance) {
    // 위, 아래, 오른쪽, 왼쪽 이동량 계산
    const up = Cartesian3.multiplyByScalar(
      colVector,
      -cellColDistance,
      new Cartesian3()
    );
    const down = Cartesian3.multiplyByScalar(
      colVector,
      cellColDistance,
      new Cartesian3()
    );
    const right = Cartesian3.multiplyByScalar(
      rowVector,
      -cellRowDistance,
      new Cartesian3()
    );
    const left = Cartesian3.multiplyByScalar(
      rowVector,
      cellRowDistance,
      new Cartesian3()
    );

    // 좌상단, 우상단, 좌하단, 우하단 좌표 계산
    const ul = Cartesian3.add(center, up, new Cartesian3());
    Cartesian3.add(ul, left, ul);
    const ur = Cartesian3.add(center, up, new Cartesian3());
    Cartesian3.add(ur, right, ur);
    const ll = Cartesian3.add(center, down, new Cartesian3());
    Cartesian3.add(ll, left, ll);
    const lr = Cartesian3.add(center, down, new Cartesian3());
    Cartesian3.add(lr, right, lr);

    return [ul, ur, lr, ll];
  }

  /*
   * 셀 꼭짓점의 평균 높이 계산. </br>
   * Calculate the average height of the input cell corner positions.
   * @private
   * @param {Cartesian3[]} positions 셀 꼭짓점. </br> Cell corner positions.
   * @param {number} [classification=Ditap.VolumeClassificationType.TERRAIN] 분석 타입. </br> Classification type.
   * @returns {number} 평균 높이. </br> Average height.
   */
  async calcCellAvgHeight(
    positions,
    classification = VolumeClassificationType.TERRAIN
  ) {
    const viewer = this.viewer;

    // 지형 로드 대기
    await viewer.terrainProvider.readyPromise;

    // 높이정보 추출
    let height = 0;
    if (classification === VolumeClassificationType.BOTH) {
      await viewer.scene
        .clampToHeightMostDetailed(
          positions
          //[...primitives, ...entities]
        )
        .then((result) => {
          let count = 0;
          //const color = Color.fromRandom()
          for (const position of result) {
            if (position instanceof Cartesian3) {
              const ctg = Cartographic.fromCartesian(position);
              height += ctg.height;
              count += 1;

              // this.createPoint(position, {
              //   minimumAlpha: 1
              // });
            }
          }

          // 추출 갯수가 2개 이상일 경우에 평균높이 계산, 아닐경우 그냥 사용
          height = count > 1 && height !== 0 ? height / count : height;
        });

      // Terrain
    } else if (classification === VolumeClassificationType.TERRAIN) {
      if (viewer.terrainProvider.availability) {
        // Cartesian3 -> Cartographic 변환, Classificaition 체크
        const newPositions = [];
        for (let i = 0; i < positions.length; i++) {
          const ctg = Cartographic.fromCartesian(positions[i]);
          newPositions.push(ctg);
        }

        await sampleTerrainMostDetailed(
          viewer.terrainProvider,
          newPositions
        ).then((result) => {
          let count = 0;
          for (const position of result) {
            if (position instanceof Cartographic) {
              height += position.height;
              count += 1;
            }
          }
          // 추출 갯수가 2개 이상일 경우에 평균높이 계산, 아닐경우 그냥 사용
          height = count > 1 && height !== 0 ? height / count : height;
        });
      }
    }
    //let height = 0;
    // if (
    //   viewer.terrainProvider instanceof CesiumTerrainProvider ||
    //   viewer.terrainProvider instanceof TerrainProvider
    // ) {

    //   //const primitives = viewer.scene.primitives._primitives;
    //   //const entities = viewer.entities.values;

    //   // Terrain + Object
    //   if (classificationType === CLASSIFICATION_TYPE.BOTH) {
    //     await viewer.scene.clampToHeightMostDetailed(
    //       positions,
    //       //[...primitives, ...entities]
    //     ).then((result) => {
    //       for (const position of result) {
    //         if (position instanceof Cartesian3) {
    //           const ctg = Cartographic.fromCartesian(position);
    //           height += ctg.height;
    //         }
    //       }

    //       // 추출 갯수가 2개 이상일 경우에 평균높이 계산, 아닐경우 그냥 사용
    //       height = result.length > 1 ? height / result.length : height;
    //     });

    //   // Terrain
    //   } else if (classificationType === CLASSIFICATION_TYPE.TERRAIN) {
    //     await sampleTerrainMostDetailed(
    //       viewer.terrainProvider,
    //       newPositions
    //     ).then((result) => {
    //       for (const position of result) {
    //         height += position.height;
    //       }

    //       // 추출 갯수가 2개 이상일 경우에 평균높이 계산, 아닐경우 그냥 사용
    //       height = result.length > 1 ? height / result.length : height;
    //     });
    //   }

    // // Terrain 없음
    // } else {
    //   height = 0;
    // }

    return height;
  }

  /*
   * 셀 체적량 계산. </br>
   * Calculate the volume of the input cell.
   * @private
   * @param {number} cellRowDistance 수평 길이. </br> Horizontal distance.
   * @param {number} cellColDistance 수직 길이. </br> Vertical distance.
   * @param {number} height 셀 높이. </br> Cell height.
   * @returns {number} 셀 체적량. </br> Cell volume
   */
  calcCellVolume(cellRowDistance, cellColDistance, height) {
    const volume = cellRowDistance * cellColDistance * height;
    return volume;
  }

  /*
   * MBR 추출. </br>
   * Extract minimum bounding rectangle.
   * @private
   * @param {Cartesian3[]} positions 좌표. </br> Area position.
   * @returns {Cartesian3[]} [좌상단, 우상단, 좌하단, 우하단]. </br> [Upper left, Upper right, lower left, lower right]
   */
  getMBR(positions) {
    // 1. 각 포인트 경위도 추출
    const lons = []; // 경도
    const lats = []; // 위도
    for (const position of positions) {
      const ct = Cartographic.fromCartesian(position);
      const lon = CesiumMath.toDegrees(ct.longitude); // 경도
      const lat = CesiumMath.toDegrees(ct.latitude); // 위도

      lons.push(lon);
      lats.push(lat);
    }

    // 2. 좌상단, 우하단 경위도 좌표 추출
    const ulx = Math.min(...lons); // 좌
    const uly = Math.max(...lats); // 상
    const lrx = Math.max(...lons); // 우
    const lry = Math.min(...lats); // 하

    // 3. 경위도 기준 MBR Cartesian 좌표 생성
    const ul = Cartesian3.fromDegrees(ulx, uly); // 좌상단
    const ur = Cartesian3.fromDegrees(lrx, uly); // 우상단
    const ll = Cartesian3.fromDegrees(ulx, lry); // 좌하단
    const lr = Cartesian3.fromDegrees(lrx, lry); // 우하단

    return [ul, ur, ll, lr];
  }

  /*
   * 카토그래픽에서 데카르트2 타입으로 변환. </br>
   * Convert Cartographic to Cartesian2.
   * @private
   * @param {Cartographic} position 카토그래픽. </br> Cartographic value.
   * @returns {Cartesian2} 데카르트2. </br> Cartesian value.
   */
  cartographicToCartesian(position) {
    const ct = Cartographic.fromCartesian(position);
    return new Cartesian2(
      CesiumMath.toDegrees(ct.longitude),
      CesiumMath.toDegrees(ct.latitude)
    );
  }

  /**
   * 체적량 계산. </br>
   * Calculate volume.
   * @param {object} options 각각 다음과 같은 속성을 가진 객체. </br> Object with the following properties:
   * @param {Cartesian3[]} options.positions 영역 좌표. </br> Volume position.
   * @param {VolumePlanHeightType | number} [options.planHeight=VolumePlanHeightType.AVERAGE] 계획고 타입 [VolumePlanHeightType.MIN, VolumePlanHeightType.MAX, VolumePlanHeightType.AVERAGE] 또는 높이 값 (미터). </br> Plan height type [VolumePlanHeightType.MIN, VolumePlanHeightType.MAX, VolumePlanHeightType.AVERAGE] or plan height value (meters).
   * @param {number} [options.cellSize=1] 셀 사이즈 (미터).  </br> Cell size (meters).
   * @param {boolean} [options.showCell=false] 셀 가시화. </br> Visualize the cell
   * @param {Color} [options.fillingVolumeColor=Color.fromBytes(85, 25, 220, 30)] 성토량 셀 색상. </br> FillVolume cell color
   * @param {Color} [options.cuttingVolumeColor=Color.fromBytes(220, 25, 105, 30)] 절토량 셀 색상. </br> CuttingVolume cell color
   * @param {VolumeClassificationType} [options.classification=VolumeClassificationType.TERRAIN] 높이 추출 유형 [VolumeClassificationType.TERRIN, VolumeClassificationType.BOTH]. </br> Height value extraction type
   * @param {boolean} [options.loading=true] 계산 중 로딩창. </br> Loading page during calculation
   * @param {function} [options.callback=undefined] 계산 종료 후 콜백 함수. </br> Callback function after volume measurement.
   * @returns {string[]} [성토량, 절토량, 합계]. </br> [Fill volume, Cutting volume, Total Volume]
   */
  async calculateVolume(options) {
    // 좌표 타입 체크
    const positions = options.positions;
    if (positions instanceof Array) {
      for (const position of positions) {
        if (!(position instanceof Cartesian3)) {
          throw Error("MeasurementVolume positions type error");
        }
      }

      if (positions.length < 3) {
        throw Error("MeasurementVolume positions length > 2");
      }
    } else {
      throw Error("MeasurementVolume positions type error");
    }

    // 계획고 체크
    let planHeight = options.planHeight || DEFAULT_OPTION.planHeight;
    if (
      !Object.values(VolumePlanHeightType).includes(planHeight) &&
      isNaN(planHeight)
    ) {
      throw Error("MeasurementVolume planHeight type error");
    }

    // 셀 크기(해상도) 체크
    const cellSize = options.cellSize || DEFAULT_OPTION.cellSize;
    if (!isNaN(cellSize)) {
      if (!(0 < cellSize)) {
        throw Error("MeasurementVolume out of range 0 < cellSize");
      }
    } else {
      throw Error("MeasurementVolume cellSize type error");
    }

    // 셀 가시화 여부 체크
    const showCell = options.showCell || DEFAULT_OPTION.showCell;
    if (typeof showCell !== "boolean") {
      throw Error("MeasurementVolume showCell type error");
    }

    // callback 체크
    const callback = options.callback || DEFAULT_OPTION.callback;
    if (!(callback instanceof Function)) {
      throw Error("MeasurementVolume callback type error");
    }

    // 성토셀 컬러 체크
    const fillingVolumeColor =
      options.fillingVolumeColor || DEFAULT_OPTION.fillingVolumeColor;
    if (!(fillingVolumeColor instanceof Color)) {
      throw Error("MeasurementVolume fillingVolumeColor type error");
    }

    // 절토셀 컬러 체크
    const cuttingVolumeColor =
      options.cuttingVolumeColor || DEFAULT_OPTION.cuttingVolumeColor;
    if (!(cuttingVolumeColor instanceof Color)) {
      throw Error("MeasurementVolume cuttingVolumeColor type error");
    }

    // 분석 방법 체크
    const classification =
      options.classification || DEFAULT_OPTION.classification;
    if (!Object.values(VolumeClassificationType).includes(classification)) {
      throw Error("MeasurementVolume classification value error");
    }

    // 로딩 가시화 여부 체크
    const loading = options.loading || DEFAULT_OPTION.loading;
    if (typeof loading !== "boolean") {
      throw Error("MeasurementVolume loading type error");
    }

    let waitLabel;
    try {
      // 0. 로딩 페이지(options.loading)
      if (loading) {
        // loading 활성화
        this._ditapLoading.open();

        // 뒤에 Primise.all 작업이 있어 loading 활성화 요청 후
        // 랜더링 체크를 하지 않고 진행하면 loading 활성화가 Propmise.all 완료 후 적용됨
        const loadingPromise = () => {
          return new Promise((resolve) => {
            const checkInterval = setInterval(() => {
              const classNames = this._ditapLoading.loading.classList;
              if (!classNames.contains("hide")) {
                clearInterval(checkInterval); // setInterval 중지
                resolve();
              }
            }, 100);
          });
        };
        await loadingPromise();
      }

      // 1. 클래스 초기화
      this.clear();

      // 계획고 타입 설정
      // VolumePlanHeightType인 경우
      if (Object.values(VolumePlanHeightType).includes(planHeight)) {
        this._planHeightType = planHeight;
        // VolumePlanHeightType이 아닌 경우 => 사용자 정의 계획고 입력
      } else if (!isNaN(planHeight)) {
        this._planHeightType = "user";
        planHeight = Number(planHeight);
        this._planHeight = planHeight;
      }

      // 2. displayCondition 설정 => 카메라 거리 2000m를 초과할 경우 비가시화
      const displayCondition = Utils.getDistanceDisplayConditon(positions);
      if (displayCondition > 2000) {
        this.displayCondition = displayCondition;
      }

      // 3. 영역 표시 엔티티 생성(점, 선, 면)
      this.createEntity(positions);

      // 4-1. 대기 라벨 생성
      const area = this.calculateArea(positions);
      const centerPosition = this.getCenterPosition(positions);
      this._centerPosition = centerPosition; // 영역 중심 좌표
      waitLabel = this.createWaitLabel(centerPosition);

      // 4-2. 측정 영역 셀 정보 생성
      const cellInfo = await this.generateCellInfo(
        positions,
        cellSize,
        classification
      );

      // 4-3. 대기 라벨 제거
      this.viewer.entities.remove(waitLabel);

      // 4-4. 면적 라벨 생성
      const label = Utils.createTotalLabel(this.viewer, area, true);
      this.clientHeight = label.clientHeight;
      label.style.right = "unset";
      this.labelEntity = label;

      this.createWindowResizeEvent();
      this.createCameraMoveEvent();

      const viewer = this.viewer;
      const labelEntity = this.labelEntity;
      const height = this.clientHeight;

      Utils.changeTotalLabelVisibility(
        viewer,
        labelEntity,
        this._centerPosition,
        displayCondition,
        height
      );

      // 5. 계획고 설정
      if (Object.values(VolumePlanHeightType).includes(this._planHeightType)) {
        planHeight = cellInfo[this._planHeightType];
      }

      // 6. 절토량, 성토량 계산
      let cuttingVolume = 0;
      let fillingVolume = 0;

      let isMessage = true;
      for (const cell of cellInfo.cellInfos) {
        //cellTo3DTriangle(this.viewer, cell.edge);

        // 셀 중심 평균 높이
        const cellHeight = cell.height;

        // 절토량 (계획고 <= 셀 평균 높이)
        if (planHeight < cellHeight) {
          cuttingVolume -= this.calcCellVolume(
            cellSize,
            cellSize,
            cellHeight - planHeight
          ); // 절토량
          //console.log(`cuttingVolume ${planHeight}, ${cellHeight}`)
          isMessage = false;
          // 성토량 (셀 평균 높이 < 계획고)
        } else if (cellHeight < planHeight) {
          fillingVolume += this.calcCellVolume(
            cellSize,
            cellSize,
            planHeight - cellHeight
          ); // 성토량
          isMessage = false;
          //console.log(`fillingVolume ${planHeight}, ${cellHeight}`)
          // 0 (셀 평균 높이 === 계획고)
        } else if (cellHeight === planHeight) {
          continue;
        }

        // 디버깅
        // let arr = cell.edge;
        // arr.push(cell.edge[0]);
        // const color = Color.fromRandom({
        // 	minimumAlpha: 1.0,
        // 	alpha: 0.6
        // });
        // this.createPoint(
        //   createGuid(),
        // 	cell.center,
        // 	color
        // );
        // this.createPolyline(
        //   createGuid(),
        // 	arr,
        // 	new ColorMaterialProperty(color)
        // );
        // this.createCellEntity(
        //   createGuid(),
        // 	new PolygonHierarchy(arr),
        // 	cellHeight < 0 ? -cellHeight : cellHeight,
        // 	new ColorMaterialProperty(color)
        // );
      }

      // 7. 셀 생성(options.showCell)
      if (showCell) {
        for (const cell of cellInfo.cellInfos) {
          const cellHeight = cell.height;

          // 절토량
          if (planHeight < cellHeight) {
            // planHeight ~ cellHeight 절토
            const cellPrimitive = this.createCell(
              cell.edge,
              planHeight,
              cellHeight - planHeight,
              cuttingVolumeColor
            );
            this.cellPrimitive.push(cellPrimitive);
            // 성토량
          } else if (cellHeight < planHeight) {
            // cellHeight ~ planHeight 성토
            const cellPrimitive = this.createCell(
              cell.edge,
              planHeight,
              -(planHeight - cellHeight),
              fillingVolumeColor
            );
            this.cellPrimitive.push(cellPrimitive);
          } else {
            const cellPrimitive = this.createCell(
              cell.edge,
              planHeight,
              0,
              DEFAULT_OPTION.planHeightVolumeColor
            );
            this.cellPrimitive.push(cellPrimitive);
          }
        }
      }
      //cellTo3DTriangle(this.viewer, cellInfo.cellInfos);

      // 5. DitapStorage에 추가
      //this.viewer.ditapStorage.measurementVolume[this._id] = this;

      // 6. set value
      this._positions = positions;
      this._planHeight = planHeight.toFixed(7);
      this._cuttingVolume = cuttingVolume.toFixed(7);
      this._fillingVolume = fillingVolume.toFixed(7);
      this._totalVolume = (fillingVolume + cuttingVolume).toFixed(7);
      this._cellSize = cellSize;
      this._showCell = showCell;
      this._fillingVolumeColor = fillingVolumeColor;
      this._cuttingVolumeColor = cuttingVolumeColor;
      this._classification = classification;
      this._loading = loading;
      this.isMessage = isMessage;

      // console.log(
      //   `성토량 : ${this._fillingVolume}\r\n절토량 : ${this._cuttingVolume}\r\n총 합 : ${this._totalVolume}`
      // );

      //this.openPopup();
      this.zoomToVolume();

      this._isReady = true;

      this._ditapLoading.close();

      if (callback instanceof Function) {
        callback(this);
      }

      return [this._fillingVolume, this._cuttingVolume, this._totalVolume];
    } catch (error) {
      console.error(error);
      if (waitLabel) {
        this.viewer.entities.remove(waitLabel);
      }
      this._ditapLoading.close();
      this.clear();
    }
  }

  /*
   * 삼각형 넓이 계산. </br>
   * Calculate triangle area.
   * @private
   * @param {Cartesian3} point1 좌표1. </br> Position1
   * @param {Cartesian3} point2 좌표2. </br> Position2
   * @param {Cartesian3} point3 좌표3. </br> Position3
   * @returns {number} 넓이. </br> Triangle area
   */
  getTriangleArea(point1, point2, point3) {
    let vec1 = Cartesian3.subtract(point2, point1, new Cartesian3());
    vec1 = Cartesian3.normalize(vec1, vec1);
    let vec2 = Cartesian3.subtract(point3, point1, new Cartesian3());
    vec2 = Cartesian3.normalize(vec2, vec2);
    const len1 = Cartesian3.distance(point1, point2);
    const len2 = Cartesian3.distance(point1, point3);

    const dot = Cartesian3.dot(vec1, vec2, new Cartesian3()); // cos
    const theta = Math.acos(dot);

    return len1 * len2 * Math.sin(theta) * 0.5;
  }

  /**
   * 영역 계산. </br>
   * Calculate the area of the created face.
   * @private
   * @param {Cartesian3[]} positions 영역 좌표. </br> Area positions.
   * @returns {string} 넓이. </br> Area.
   */
  calculateArea(positions) {
    let totalArea = 0;

    const cartographics = [];
    /** 입력된 point가 시계방향인지 반시계방향인지 확인  */
    for (let i = 0, len = positions.length; i < len; i++) {
      const cartographic = Cartographic.fromCartesian(positions[i]);
      cartographics.push(
        new Cartesian2(cartographic.longitude, cartographic.latitude)
      );
    }

    const triangles = PolygonPipeline.triangulate(cartographics);
    for (let i = 0, len = triangles.length; i < len; i += 3) {
      const p = positions[triangles[i]];
      const q = positions[triangles[i + 1]];
      const r = positions[triangles[i + 2]];
      const area = this.getTriangleArea(p, q, r);
      totalArea += area;
    }

    return totalArea.toFixed(2);
  }

  /*
   * 영역 중심점 추출. </br>
   * Extract area centroids.
   * @private
   * @param {Cartesian3[]} positions 영역 좌표. </br> Area position.
   * @returns {Cartesian3} 중심점. </br> Center position
   */
  getCenterPosition(positions) {
    return BoundingSphere.fromPoints(positions).center;
  }

  /*
   * 윈도우 크기 조절 이벤트 제거.</br>
   * Create the window resize event.
   */
  removeChangeEvent() {
    if (this.cameraChangedEvent) {
      this.cameraChangedEvent();
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    if (this.windowResizeEvent) {
      window.removeEventListener("resize", this.windowResizeEvent);
    }
  }

  /*
   * 윈도우 크기 조절 시 이벤트 생성.</br>
   * Create an event for window resize.
   */
  createWindowResizeEvent() {
    const viewer = this.viewer;
    const container = this.viewer.container;
    const labelEntity = this.labelEntity;
    const positions = this._centerPosition;
    const height = this.clientHeight;
    const displayCondition = this.displayCondition;
    const resizeOriginal = () => {
      Utils.changeTotalLabelVisibility(
        viewer,
        labelEntity,
        positions,
        displayCondition,
        height
      );
    };

    this.windowResizeEvent = () => {
      resizeOriginal();
      setTimeout(resizeOriginal, 500);
    };

    window.addEventListener("resize", this.windowResizeEvent);

    // ResizeObserver 객체를 생성하고 콜백 함수 전달.
    this.resizeObserver = new ResizeObserver(resizeOriginal);

    // 대상 요소에 대한 크기 변화 관찰.
    this.resizeObserver.observe(container);
  }

  /*
   * 카메라 시점 이동 시 이벤트 생성. </br>
   * Create an event when moving the camera point of view.
   * @private
   */
  createCameraMoveEvent() {
    const viewer = this.viewer;
    const labelEntity = this.labelEntity;
    const positions = this._centerPosition;
    const height = this.clientHeight;
    const displayCondition = this.displayCondition;

    // 카메라 이동 이벤트 생성
    this.cameraChangedEvent = viewer.camera.changed.addEventListener(() => {
      Utils.changeTotalLabelVisibility(
        viewer,
        labelEntity,
        positions,
        displayCondition,
        height
      );
    });
  }
}

export default MeasurementVolume;
